import {FormControl, FormField} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {Path, useController} from 'react-hook-form';

import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  convertHtmlToFormat,
  countMobileDeMarkupOverhead,
  DEFAULT_PLATFORM_FORMAT,
  PLATFORM_FORMAT_MAP,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useAdvertisementPlatformFilter} from '../hooks/useAdvertisementPlatformFilter';
import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';

interface DescriptionFormFieldProps extends TestIdProps {
  vehicleId: string;
  control: FormControl<GeneralSettingsFormValues>;
  descriptionLabel?: string;
  nameOverride?: Path<GeneralSettingsFormValues>;
  isDisabled?: boolean;
}

export function DescriptionFormField(props: DescriptionFormFieldProps) {
  const fieldName = props.nameOverride ?? 'description';
  const {field} = useController({name: fieldName, control: props.control});
  const fieldValue = typeof field.value === 'string' ? field.value : null;

  const {platforms} = useAdvertisementPlatformFilter({
    vehicleId: props.vehicleId,
  });

  const enabledPlatforms = platforms.filter((platform) => platform.enabled);

  return (
    <VStack spacing={1}>
      <Box minHeight={50}>
        <FormField
          control={props.control}
          label={props.descriptionLabel}
          placeholder={i18n.t('page.advertisement.labels.descriptionPlaceholder')}
          name={fieldName}
          type="richText"
          isScrollable
          isDisabled={props.isDisabled}
          rows={7}
          toolbarOptions={['bold', 'italic', 'underline', 'listOrdered', 'listBullet', 'emoji']}
          data-testid={suffixTestId('description', props)}
        />
      </Box>
      <HStack spacing={3} align="center" wrap>
        {enabledPlatforms.map((platform) => {
          const config = PLATFORM_FORMAT_MAP[platform.platform.code] ?? DEFAULT_PLATFORM_FORMAT;
          const {characterLimit} = config;

          if (!isNotNil(characterLimit)) {
            return null;
          }

          const converted = convertHtmlToFormat(fieldValue ?? '', config.format);
          const isMobileDe = config.format === 'mobileDe';
          const markupOverhead = isMobileDe ? countMobileDeMarkupOverhead(converted) : 0;
          const count = isMobileDe ? converted.length - markupOverhead : converted.length;

          const isOverLimit = count > characterLimit;
          const MARKUP_OVERHEAD_RATIO = 0.1;
          const isMarkupOverLimit =
            isMobileDe && markupOverhead > characterLimit * MARKUP_OVERHEAD_RATIO;

          return (
            <HStack key={platform.platform.code} spacing={1} align="center">
              <Box
                width={2}
                height={2}
                borderRadius="medium"
                backgroundColor={
                  isOverLimit || isMarkupOverLimit ? 'severity.warning' : 'severity.success'
                }
              />
              <Text size="xSmall" color="secondary">
                {isMarkupOverLimit
                  ? `${platform.platform.title} (${count} / ${characterLimit}) — ${i18n.t('page.advertisement.labels.markupOverheadExceeded')}`
                  : `${platform.platform.title} (${count} / ${characterLimit})`}
              </Text>
            </HStack>
          );
        })}
      </HStack>
    </VStack>
  );
}
